import { Injectable } from '@angular/core';
import { Urls } from '../models/urls';
import { User } from '../models/user';
import { OrgSiteSelectionService } from './org-site-selection/org-site-selection.service';
import { RoleEnum } from '@app/api-module/user-management-api-client-v2';

@Injectable({
    providedIn: 'root'
})
export class PermissionRoutingService {
    constructor(private readonly orgSiteSelectionService: OrgSiteSelectionService) {}

    public static checkUserPermission(user: User, role: RoleEnum, orgCode: string): boolean {
        return user?.hasRole(role, orgCode) ?? false;
    }

    getNextAvailableRoute(user: User, currentRoute: string, orgCode?: string) {
        if (!orgCode) {
            orgCode = this.orgSiteSelectionService.getOrgSelectedValue()?.orgCode ?? user.orgs ? Array.from(user.orgs.keys())[0] : null;
        }

        if (
            currentRoute !== Urls.DASHBOARD &&
            (PermissionRoutingService.checkUserPermission(user, RoleEnum.GeneralAccess, orgCode) ||
                PermissionRoutingService.checkUserPermission(user, RoleEnum.ClinicalFeedback, orgCode) ||
                PermissionRoutingService.checkUserPermission(user, RoleEnum.ImagingRequestViewer, orgCode) ||
                PermissionRoutingService.checkUserPermission(user, RoleEnum.ImagingRequestEditor, orgCode) ||
                PermissionRoutingService.checkUserPermission(user, RoleEnum.DemandManagement, orgCode))
        ) {
            return Urls.DASHBOARD;
        } else if (currentRoute !== Urls.MANAGE_USERS && PermissionRoutingService.checkUserPermission(user, RoleEnum.Admin, orgCode)) {
            return Urls.MANAGE_USERS;
        } else {
            return null;
        }
    }

    canAccessRoute(user: User, route: string): boolean {
        const orgCode = this.orgSiteSelectionService.getOrgSelectedValue()?.orgCode ?? Array.from(user.orgs.keys())[0];
        switch (route) {
            case Urls.MANAGE_USERS:
                // filter out if no org has userManagement feature
                return PermissionRoutingService.checkUserPermission(user, RoleEnum.Admin, orgCode);

            case Urls.MY_DETAILS:
                return true;

            // Urls.WORKLIST is for backwards compatibility only. Should be removed later once new route changes are deployed to production.
            case Urls.WORKLIST:
            case Urls.DASHBOARD:
                return (
                    PermissionRoutingService.checkUserPermission(user, RoleEnum.GeneralAccess, orgCode) ||
                    PermissionRoutingService.checkUserPermission(user, RoleEnum.ClinicalFeedback, orgCode) ||
                    PermissionRoutingService.checkUserPermission(user, RoleEnum.ImagingRequestViewer, orgCode) ||
                    PermissionRoutingService.checkUserPermission(user, RoleEnum.ImagingRequestEditor, orgCode) ||
                    PermissionRoutingService.checkUserPermission(user, RoleEnum.DemandManagement, orgCode)
                );

            case Urls.DASHBOARD_ORDERS:
                return PermissionRoutingService.checkUserPermission(user, RoleEnum.GeneralAccess, orgCode);

            case Urls.DASHBOARD_REFERRALS:
                return PermissionRoutingService.checkUserPermission(user, RoleEnum.GeneralAccess, orgCode);

            case Urls.DASHBOARD_CLINICAL_FEEDBACK:
                return PermissionRoutingService.checkUserPermission(user, RoleEnum.ClinicalFeedback, orgCode);

            case Urls.DASHBOARD_DEMAND_MANAGEMENT:
                return PermissionRoutingService.checkUserPermission(user, RoleEnum.DemandManagement, orgCode);

            case Urls.DASHBOARD_IMAGING_REQUESTS:
                return (
                    PermissionRoutingService.checkUserPermission(user, RoleEnum.ImagingRequestViewer, orgCode) ||
                    PermissionRoutingService.checkUserPermission(user, RoleEnum.ImagingRequestEditor, orgCode)
                );

            case Urls.PASSWORD_CHANGE:
                return true;
        }

        return false;
    }
}
